import {
    BooleanInfo,
    ConfigInfo,
    CurveInfo,
    ImageInfo,
    type InterfaceDescriptor,
    JsonInfo,
    MaterialInfo,
    MeshInfo,
    NodesInfo,
    NumberInfo,
    ObjectInfo,
    StringInfo,
    TemplateInfo,
} from "#template/interface-descriptors"
import {Parameters} from "#template/nodes/parameters"
import type {AnyJsonValue} from "#template/value-types"

export type VariationScope = "local" | "global"

export type AnyInterfaceDescriptor = InterfaceDescriptor<unknown, {}>

// Only config-variant selections form a configuration; any other input kind would break the render job assignment keys.
export function configurationFromDescriptors(descriptors: readonly AnyInterfaceDescriptor[], includeAllSubTemplateInputs: boolean): Parameters {
    const configuration: {[paramId: string]: AnyJsonValue} = {}

    for (const descriptor of descriptors) {
        if (descriptor.props.type !== "input") continue
        if (descriptor.props.isSetByTemplate && !includeAllSubTemplateInputs) continue

        const {id} = descriptor.props
        if (descriptor.props.value === null) continue

        if (descriptor instanceof ConfigInfo) configuration[id] = descriptor.props.value!.id
        else if (
            descriptor instanceof ObjectInfo ||
            descriptor instanceof MeshInfo ||
            descriptor instanceof CurveInfo ||
            descriptor instanceof MaterialInfo ||
            descriptor instanceof TemplateInfo ||
            descriptor instanceof ImageInfo ||
            descriptor instanceof StringInfo ||
            descriptor instanceof NumberInfo ||
            descriptor instanceof BooleanInfo ||
            descriptor instanceof JsonInfo ||
            descriptor instanceof NodesInfo
        ) {
            continue
        } else throw new Error("Invalid descriptor type")
    }

    return new Parameters({parameters: configuration})
}

export type VariationPart = {
    key: string
    group: string
    variant: string
    variantId: string
}

export function configurationParts(configInfos: readonly ConfigInfo[], selection: Readonly<Record<string, string>> = {}): VariationPart[] {
    const parts: VariationPart[] = []
    for (const descriptor of configuratorOrder(configInfos, (configInfo) => configInfo.props)) {
        const {id, name, value, variants, isSetByTemplate} = descriptor.props
        if (!value || isSetByTemplate) continue
        const variantId = selection[id] ?? value.id
        const variant = variants.find((v) => v.id === variantId)
        parts.push({key: id, group: name, variant: variant?.name ?? value.name, variantId})
    }
    return parts
}

export function byOrder(a: number, b: number) {
    return a === b ? 0 : a - b
}

// Must place sections as the configurator menu does (groupBySectionTitle in the web app).
export function configuratorOrder<T>(items: Iterable<T>, propsOf: (item: T) => {order?: number; sectionTitle?: string}): T[] {
    const sorted = [...items].sort((a, b) => byOrder(propsOf(a).order ?? Infinity, propsOf(b).order ?? Infinity))
    const sections = new Map<string, T[]>()
    for (const item of sorted) {
        const {sectionTitle} = propsOf(item)
        if (!sectionTitle) continue
        const members = sections.get(sectionTitle)
        if (members) members.push(item)
        else sections.set(sectionTitle, [item])
    }
    return sorted.flatMap((item) => {
        const {sectionTitle} = propsOf(item)
        const members = sectionTitle ? sections.get(sectionTitle)! : [item]
        return members[0] === item ? members : []
    })
}

export function variationName(parts: readonly VariationPart[]): string {
    return parts.length > 0 ? parts.map(({group, variant}) => `${group}: ${variant}`).join(", ") : "Single Variation"
}

export function configurationName(configInfos: readonly ConfigInfo[]): string {
    return variationName(configurationParts(configInfos))
}
